import { produce } from 'immer';

import { RESET_STATE } from 'store/global/actions';

import {
  ADD_DOCUMENT_EXTRACTION_FIELDS_TYPE,
  DELETE_DOCUMENT_EXTRACTION_FIELDS_TYPE,
  EDIT_DOCUMENT_EXTRACTION_FIELDS_TYPE,
  GET_DOCUMENT_EXTRACTION_FIELDS_TYPE,
  GET_DOCUMENT_EXTRACTION_FIELDS_TYPE_BY_ID,
  GET_INSPECTOR_EXTRACTION_FIELDS_BY_TYPE,
} from '../actions';

const initialState = {
  fields: {},
  inspectorFieldsByTypeId: {},
};

// The document-tag (discriminator) flag is last-set-wins across a document type: at most one
// field may be flagged. Find the field the just-stored payload marked, then clear the flag on
// every other field so the UI reflects the exclusivity optimistically (the server is authoritative).
const findDiscriminatorFieldId = fields =>
  Object.keys(fields).find(fieldId => fields[fieldId]?.isDiscriminator) ?? null;

const clearDiscriminatorOnOtherFields = (fields, activeFieldId) => {
  Object.keys(fields).forEach(fieldId => {
    if (fieldId !== activeFieldId && fields[fieldId]?.isDiscriminator) {
      fields[fieldId].isDiscriminator = false;
    }
  });
};

const documentExtractionFieldsReducer = (state = initialState, { type, payload }) => {
  // Legacy actions operate on the flat entity map (state.fields).
  // New inspector action stores raw flat arrays keyed by documentTypeId
  // in state.inspectorFieldsByTypeId — isolated from the shared entity map.
  switch (type) {
    case GET_DOCUMENT_EXTRACTION_FIELDS_TYPE:
      return produce(state, draft => {
        draft.fields = payload.documentExtractionFields || {};
      });

    case ADD_DOCUMENT_EXTRACTION_FIELDS_TYPE:
    case GET_DOCUMENT_EXTRACTION_FIELDS_TYPE_BY_ID:
      return produce(state, draft => {
        Object.assign(draft.fields, payload.documentExtractionFields);

        const activeFieldId = findDiscriminatorFieldId(payload.documentExtractionFields ?? {});

        if (activeFieldId) {
          clearDiscriminatorOnOtherFields(draft.fields, activeFieldId);
        }
      });

    case EDIT_DOCUMENT_EXTRACTION_FIELDS_TYPE:
      return produce(state, draft => {
        draft.fields[payload.id] = payload;

        if (payload.isDiscriminator) {
          clearDiscriminatorOnOtherFields(draft.fields, payload.id);
        }
      });

    case DELETE_DOCUMENT_EXTRACTION_FIELDS_TYPE:
      return produce(state, draft => {
        delete draft.fields[payload];
      });

    case GET_INSPECTOR_EXTRACTION_FIELDS_BY_TYPE:
      return produce(state, draft => {
        draft.inspectorFieldsByTypeId[payload.documentTypeId] = payload.fields;
      });

    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default documentExtractionFieldsReducer;
